import type { TargetAndTransition, Transition } from 'framer-motion'

export type AnimationVariant = {
  initial: TargetAndTransition
  animate: TargetAndTransition
  exit: TargetAndTransition
}

// --- Transitions ---

/** Quick spring with no bounce. Most common transition. */
export const spring: Transition = { type: 'spring', bounce: 0, duration: 0.2 }

/** Slightly longer spring. Good for content reveals. */
export const springGentle: Transition = { type: 'spring', bounce: 0, duration: 0.3 }

/** Stiff spring for snappy horizontal swap animations. */
export const springStiff: Transition = { type: 'spring', stiffness: 500, damping: 30 }

// --- Easings ---

/** Smooth ease-out curve. Good for content entrances. */
export const easeOut: [number, number, number, number] = [0.22, 1, 0.36, 1]

/** Ease-out over 0.4s. Shared transition for coordinated height/content animations
 *  (e.g. the action-flow widget and the order result-banner reveal). `satisfies` keeps
 *  `duration` concretely typed so consumers can derive from it (e.g. a staggered delay). */
export const easeOutTransition = { duration: 0.4, ease: easeOut } satisfies Transition

// --- Variants ---

/** Simple opacity fade. */
export const fadeIn: AnimationVariant = {
  initial: { opacity: 0 },
  animate: { opacity: 1 },
  exit: { opacity: 0 }
}

/** Opacity fade with 4px blur. */
export const fadeBlurIn: AnimationVariant = {
  initial: { opacity: 0, filter: 'blur(4px)' },
  animate: { opacity: 1, filter: 'blur(0px)' },
  exit: { opacity: 0, filter: 'blur(4px)' }
}

/** Fade + 4px vertical slide. */
export const fadeSlideUp: AnimationVariant = {
  initial: { opacity: 0, y: 4 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -4 }
}

/** Fade + 4px vertical slide with 4px blur. */
export const fadeSlideBlurIn: AnimationVariant = {
  initial: { opacity: 0, y: 4, filter: 'blur(4px)' },
  animate: { opacity: 1, y: 0, filter: 'blur(0px)' },
  exit: { opacity: 0, y: -4, filter: 'blur(4px)' }
}

/** Width collapse with opacity. Animates from width:0 to width:auto. */
export const collapseWidth: AnimationVariant = {
  initial: { width: 0, opacity: 0 },
  animate: { width: 'auto', opacity: 1 },
  exit: { width: 0, opacity: 0 }
}

/** Height collapse with opacity. Pair with `className="overflow-hidden"`. */
export const collapseHeight: AnimationVariant = {
  initial: { height: 0, opacity: 0 },
  animate: { height: 'auto', opacity: 1 },
  exit: { height: 0, opacity: 0 }
}

/** Subtle scale + 4px blur for list items or presence toggles. */
export const scaleBlurIn: AnimationVariant = {
  initial: { scale: 0.975, opacity: 0, filter: 'blur(4px)' },
  animate: { scale: 1, opacity: 1, filter: 'blur(0px)' },
  exit: { scale: 0.975, opacity: 0, filter: 'blur(4px)' }
}
